/**
 * roles.js — canonical role + access-level module (role refactor, Phase 0/1).
 *
 * SINGLE SOURCE OF TRUTH for the platform's role vocabulary. Every client-side
 * role check imports from here. The server has a CommonJS twin at
 * `functions/src/util/roles.js` — keep the two in sync (same canonical list,
 * same access-level mapping). If you edit one, edit the other.
 *
 * Two-layer model:
 *   - `roles: string[]` on users/{uid} is the GRANULAR source of truth (a user
 *     can hold several roles, e.g. ['agent_services', 'trainer']).
 *   - the Firebase Auth custom claim carries only a coarse `accessLevel`
 *     (super_admin | admin | member). Only super_admin and admin are elevated;
 *     every other granular role maps to `member`. Firestore rules read
 *     accessLevel; the granular roles drive UI capability flags.
 *
 * RW-698: `broker` and `office_coordinator` are first-class canonical roles
 * here so the onboarding assignee tokens of the same name resolve.
 */

export const PERMANENT_SUPER_ADMIN_EMAIL = 'clint@reverealtors.com';

// Canonical granular roles, ordered MOST → LEAST privileged. Order matters for
// label/priority display and for any "highest role wins" derivation.
export const CANONICAL_ROLES = [
  'super_admin',
  'admin',
  'agent_services',
  'trainer',
  'tc',
  'broker',
  'office_coordinator',
  'agent',
  'staff_self', // Phase 5: office-staff self-service login (least privileged; MEMBER access)
];

const CANONICAL_ROLE_SET = new Set(CANONICAL_ROLES);

// Coarse access tiers carried in the custom claim.
export const ACCESS_LEVELS = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  MEMBER: 'member',
};

// Default granular roles for a brand-new user.
export const DEFAULT_ROLES = ['agent'];

// Human labels for each canonical role (UI surfaces).
export const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  agent_services: 'Agent Services',
  trainer: 'Trainer',
  tc: 'TC',
  broker: 'Broker',
  office_coordinator: 'Office Coordinator',
  agent: 'Agent',
  staff_self: 'Office Staff',
};

/**
 * RW-1202: a stored role → its display label. Tolerant of legacy capital-case
 * values (a pre-RW-1202 `role: 'Agent'`). RW-3540: moved here from
 * PeopleDirectory so the Org Chart list labels roles the same way.
 */
export function roleLabel(role) {
  const canon = String(role || 'agent').toLowerCase();
  return ROLE_LABELS[canon] || (canon.charAt(0).toUpperCase() + canon.slice(1).replace(/_/g, ' '));
}

/**
 * RW-3540: the one role a list shows for a person. The singular `role` is
 * roles[0] in write order, so it is not the answer: take the first role they
 * hold in CANONICAL_ROLES order. An agent whose title names an org tier (most
 * brokers are stored role 'agent' with title "Managing Broker" — RW-1404)
 * shows that tier, as the Broker/Manager directory chips match it.
 */
export function personRoleLabel(user) {
  const held = [user?.role, ...(Array.isArray(user?.roles) ? user.roles : [])]
    .filter(Boolean).map((r) => String(r).toLowerCase());
  const top = CANONICAL_ROLES.find((r) => held.includes(r)) || held[0] || 'agent';
  if (top === 'agent') {
    const title = String(user?.title || '');
    if (/broker/i.test(title)) return 'Broker';
    if (/manager|managing/i.test(title)) return 'Manager';
  }
  return roleLabel(top);
}

export function isValidRole(role) {
  return typeof role === 'string' && CANONICAL_ROLE_SET.has(role);
}

/**
 * RW-1331: recover a canonical role from a STORED value whose case or padding
 * drifted. Legacy docs carry pre-RW-1202 values like `role: 'Agent'`; case is
 * not a role distinction, so `'Agent'`, `' agent '` and `'AGENT'` all resolve
 * to `agent`. Anything that is not a canonical role once folded returns null.
 *
 * This is deliberately NOT `isValidRole`, which stays strict: input arriving at
 * a write boundary (`setUserRole`) must still be rejected rather than guessed
 * at, so the canonical-lowercase invariant of Gotcha #5 keeps holding for new
 * writes. Tolerance belongs on the read side only.
 */
export function canonicalizeRole(role) {
  if (typeof role !== 'string') return null;
  const folded = role.trim().toLowerCase();
  return CANONICAL_ROLE_SET.has(folded) ? folded : null;
}

/**
 * Normalize string | string[] | null/undefined → array of valid canonical
 * roles, deduped, original order preserved. Unknown/legacy values are dropped.
 *
 * STRICT BY DESIGN — do not make this case-tolerant (RW-1331). It feeds
 * `resolveProvisionedRoles` and the server staff gates, which derive privilege
 * from doc data; folding case there would let a drifted `role: 'Admin'` grant
 * admin, which the RW-1699 input-hygiene test pins as untrusted. Gotcha #5:
 * surface a non-canonical role, don't teach the code to accept it. Drifted
 * STORED values are healed by a backfill, and `canonicalizeRole` above exists
 * to audit them — not to launder them into a privilege decision.
 */
export function normalizeRoles(input) {
  const arr = Array.isArray(input) ? input : input == null ? [] : [input];
  const out = [];
  const seen = new Set();
  for (const r of arr) {
    if (isValidRole(r) && !seen.has(r)) {
      seen.add(r);
      out.push(r);
    }
  }
  return out;
}

export function hasRole(roles, role) {
  return normalizeRoles(roles).includes(role);
}

export function hasAnyRole(roles, candidates) {
  const set = new Set(normalizeRoles(roles));
  return (candidates || []).some((r) => set.has(r));
}

/**
 * RW-2444: the roles a user DOC holds — for grouping and display only, never
 * a privilege decision (gates read accessLevel / the claim). Read exactly as
 * the server reads a caller (resolveCaller): roles[] when the doc has one,
 * else the singular `role`, exact role names only. The singular `role` is
 * roles[0] kept for legacy readers, so reading it alone filed the firm's
 * broker (`[agent, broker]`) under Agents and its TC (`[admin, tc]`) as an
 * admin only. No case-folding: a screen that says who can act on a deal must
 * not credit a role the server ignores (a stray `role: 'Broker'` is not a
 * broker). Prod's three legacy `role: 'Agent'` docs all carry roles: [agent].
 */
export function userRoles(user) {
  if (!user) return [];
  return normalizeRoles(user.roles != null ? user.roles : user.role);
}

/**
 * RW-3909: a broker, as the IC Agreement's countersigning pool counts one —
 * the `broker` role (read as userRoles reads it), or a title naming the
 * Broker tier (most brokers are stored role 'agent' with title "Managing
 * Broker" — RW-1404; matched as the People directory's Broker chip matches
 * it). The server decides the pool (functions-integrations/docuseal/
 * brokerPool.js isBrokerTier); roles.brokerTier.rw3909.test.js keeps the two
 * in step.
 */
export function isBrokerTier(user) {
  if (!user) return false;
  return userRoles(user).includes('broker') || /broker/i.test(String(user.title || ''));
}

/**
 * Coarse access level a single granular role grants. Anything that is not
 * super_admin or admin grants only `member`.
 */
export function accessLevelForRole(role) {
  if (role === 'super_admin') return ACCESS_LEVELS.SUPER_ADMIN;
  if (role === 'admin') return ACCESS_LEVELS.ADMIN;
  return ACCESS_LEVELS.MEMBER;
}

/**
 * Coarse access level for a SET of roles = the highest any role grants.
 * ['agent', 'admin'] → 'admin'; ['agent', 'trainer'] → 'member'.
 */
export function accessLevelForRoles(roles) {
  const norm = normalizeRoles(roles);
  if (norm.includes('super_admin')) return ACCESS_LEVELS.SUPER_ADMIN;
  if (norm.includes('admin')) return ACCESS_LEVELS.ADMIN;
  return ACCESS_LEVELS.MEMBER;
}

/** True when an access level is elevated (admin or super_admin). */
export function isElevated(accessLevel) {
  return (
    accessLevel === ACCESS_LEVELS.SUPER_ADMIN ||
    accessLevel === ACCESS_LEVELS.ADMIN
  );
}
